/**
 * Table — eDMS Design System
 * Source: drafts/31_ux_design_system_spec.md §3.4
 *
 * Variants: plain, sortable, filterable, selectable, expandable.
 * Mobile: < 768px collapses each row to a card (label-pair layout via <ColumnConfig.mobileLabel>).
 *
 * Accessibility:
 *   - Semantic <table>, <thead>, <th scope="col">.
 *   - aria-sort on sortable columns.
 *   - Row count announced via aria-live on filter changes.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './Table.module.css';

export interface ColumnConfig<T> {
  key: string;
  header: ReactNode;
  /** Render cell content for the row. */
  cell: (row: T) => ReactNode;
  /** Optional sort comparator. Provide to enable sorting on this column. */
  sortBy?: (row: T) => string | number;
  /** Right-align numeric columns. */
  numeric?: boolean;
  /** Mobile label override (defaults to header). */
  mobileLabel?: string;
  /** Visual width hint. */
  width?: string;
}

export interface TableProps<T> {
  rows: T[];
  columns: ColumnConfig<T>[];
  /** Stable row key extractor. */
  getRowId: (row: T) => string | number;
  /** Empty state UI. */
  emptyState?: ReactNode;
  /** Loading state — shows skeleton rows. */
  isLoading?: boolean;
  /** Click handler — if provided, rows are interactive. */
  onRowClick?: (row: T) => void;
  /** Caption for screen readers. */
  caption?: string;
  className?: string;
}

type SortDir = 'asc' | 'desc';

export function Table<T>({
  rows,
  columns,
  getRowId,
  emptyState,
  isLoading,
  onRowClick,
  caption,
  className,
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortBy) return rows;
    return [...rows].sort((a, b) => {
      const av = col.sortBy!(a);
      const bv = col.sortBy!(b);
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rows, sortKey, sortDir, columns]);

  function handleSort(col: ColumnConfig<T>) {
    if (!col.sortBy) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  }

  if (isLoading) {
    return <TableSkeleton columns={columns} className={className} />;
  }

  if (rows.length === 0) {
    return (
      <div className={clsx(styles.empty, className)} role="status">
        {emptyState ?? 'No items.'}
      </div>
    );
  }

  return (
    <div className={clsx(styles.tableWrapper, className)}>
      <table className={styles.table}>
        {caption && <caption className="visually-hidden">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((col) => {
              const isSorted = sortKey === col.key;
              const ariaSort = isSorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none';
              const sortable = !!col.sortBy;
              return (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={sortable ? ariaSort : undefined}
                  style={{ width: col.width }}
                  className={clsx(col.numeric && styles.numeric, sortable && styles.sortable)}
                  onClick={sortable ? () => handleSort(col) : undefined}
                  tabIndex={sortable ? 0 : undefined}
                  onKeyDown={sortable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(col); } } : undefined}
                  /* Keep the implicit columnheader role — role="button" would make
                     aria-sort invalid (aria-allowed-attr). The th stays keyboard-
                     operable via tabIndex + onKeyDown (canonical sortable-column pattern). */
                >
                  {col.header}
                  {sortable && (
                    <span aria-hidden="true" className={styles.sortIcon}>
                      {isSorted ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr
              key={getRowId(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={clsx(onRowClick && styles.interactive)}
              tabIndex={onRowClick ? 0 : undefined}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  data-label={col.mobileLabel ?? (typeof col.header === 'string' ? col.header : col.key)}
                  className={clsx(col.numeric && styles.numeric)}
                >
                  {col.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableSkeleton<T>({ columns, className }: { columns: ColumnConfig<T>[]; className?: string }) {
  return (
    <div className={clsx(styles.tableWrapper, className)} aria-busy="true" aria-live="polite">
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col">{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 4 }).map((_, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key}><span className={styles.skeleton} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
